import * as styles from "./TypedAddressList.css";
import { listCard } from "./fields.css";

import { useAtomValue } from "jotai";

import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import { useArrayField, useField } from "@frontend/hooks/form";
import {
  CityField,
  CountryField,
  ExtendedAddressField,
  PostalCodeField,
  StateField,
  StreetField,
  useAddressAutofill,
} from "@frontend/shared/address-fields";
import { Trans } from "@lingui/react/macro";

import { useContactForm } from "../../hooks/useContactForm";
import { ContactAddressPlaceLabels } from "../../i18n";
import { isEditModeAtom } from "../../stores";
import type {
  ContactTypedAddress,
  TypedAddressListItemField,
} from "../../types";
import { AddButton } from "./AddButton";
import { ClipboardButton } from "./ClipboardButton";
import { FieldList } from "./FieldList";
import { Place } from "./Place";

export function TypedAddressList() {
  const { contactForm } = useContactForm();

  const field = contactForm.fields.typedAddresses;

  const rows = useArrayField(field);

  const editMode = useAtomValue(isEditModeAtom);

  const removeHandler = (index: number) => {
    rows.remove(rows.itemAtoms[index]!);
  };

  const insertHandler = () => {
    rows.append({
      place: "",
      street: "",
      postalCode: "",
      city: "",
      country: "",
      state: "",
    } as ContactTypedAddress);
  };

  return (
    <Fieldset variant="list" orientation="horizontal" className={listCard}>
      <FieldsetLegend
        count={rows.items.length}
        action={editMode && <AddButton onClick={insertHandler} />}
      >
        <Trans>Addresses</Trans>
      </FieldsetLegend>
      <FieldList
        field={field}
        onRemove={removeHandler}
        fieldAlign="start"
        renderLeft={(item) => (
          <Place field={item.fields.place} labels={ContactAddressPlaceLabels} />
        )}
        renderRight={(item) => <TypedAddressListItemContent item={item} />}
        renderEmpty={() => <Trans>No addresses</Trans>}
      />
    </Fieldset>
  );
}

function TypedAddressListItemContent({
  item,
}: {
  item: TypedAddressListItemField;
}) {
  const editMode = useAtomValue(isEditModeAtom);

  const street = useField(item.fields.street);
  const extendedAddress = useField(item.fields.extendedAddress);
  const postalCode = useField(item.fields.postalCode);
  const city = useField(item.fields.city);
  const country = useField(item.fields.country);
  const state = useField(item.fields.state);

  const formattedAddress = [
    street.value,
    extendedAddress.value,
    [postalCode.value, city.value].filter(Boolean).join(" "),
    state.value,
    country.value,
  ]
    .filter(Boolean)
    .join(", ");

  if (editMode) {
    return <TypedAddressListItemFields item={item} />;
  }

  return (
    <>
      <div className={styles.addressLines}>
        {street.value && <span>{street.value}</span>}
        {extendedAddress.value && <span>{extendedAddress.value}</span>}
        {(postalCode.value || city.value) && (
          <span>
            {[postalCode.value, city.value].filter(Boolean).join(" ")}
          </span>
        )}
        {state.value && <span>{state.value}</span>}
        {country.value && <span>{country.value}</span>}
      </div>
      <ClipboardButton value={formattedAddress} />
    </>
  );
}

/**
 * The same lines, writable, completed from the referential. No labels: the
 * order is the one a letter is written in, as the read-only view shows it.
 */
function TypedAddressListItemFields({
  item,
}: {
  item: TypedAddressListItemField;
}) {
  const fields = item.fields;

  const autofill = useAddressAutofill(fields);

  return (
    <div className={styles.addressFields}>
      <StreetField
        field={fields.street}
        countryField={fields.country}
        size="sm"
        onAddressSelected={autofill.onAddressSelected}
      />

      <ExtendedAddressField field={fields.extendedAddress} size="sm" />

      <div className={styles.postalCityRow}>
        <PostalCodeField field={fields.postalCode} size="sm" />

        <CityField
          field={fields.city}
          countryField={fields.country}
          size="sm"
          onCitySelected={autofill.onCitySelected}
          onCityCleared={autofill.onCityCleared}
        />
      </div>

      <StateField
        field={fields.state}
        countryField={fields.country}
        size="sm"
      />

      <CountryField field={fields.country} size="sm" />
    </div>
  );
}
